import React, { useState, useMemo, useEffect } from "react";
import { observer } from "mobx-react-lite";
import cx from "classnames";
import styles from "./index.module.scss";
import { Util, Hooks, Request, Cookie, Storage, XtLocale } from "@xt/xtbase";
import copy from "clipboard-copy";
import store from "store";
import { get_enterpriseApplyReason } from "@/api/xt-user";
import { getSecurityLevel } from "api/uaapi/user";
import { get_kycWalletLimit } from "api/xt4/wallet";
import axios, { waitRes } from "utils/axios";
import { useRouter } from "next/router";
import { Drawer, Input, message } from "antd";
import Geren from "@/assets/images/account/overview-setting.svg";
import SafetyImg from "@/assets/images/account/overview-security.svg";
import CertificationImg from "@/assets/images/account/overview-verification.svg";
import InviteImg from "@/assets/images/account/overview-invite.svg";
import ApiImg from "@/assets/images/account/overview-about.svg";
import FeeImg from "@/assets/images/account/overview-fee.svg";
import PaymentMethodImg from "@/assets/images/account/overview-payment.svg";
import SpikexCardImg from "@/assets/images/account/overview-spikex-card.svg";
import overviewDefaultHeadImg from "@/assets/images/setting/head.svg";
import Lv1Img from "@/assets/images/account/lv1.png";
import Lv2Img from "@/assets/images/account/lv2.png";
import Lv3Img from "@/assets/images/account/lv3.png";
import Lv4Img from "@/assets/images/account/lv4.png";
import Lv5Img from "@/assets/images/account/lv5.png";
import Lv6Img from "@/assets/images/account/lv6.png";
import Lv7Img from "@/assets/images/account/lv7.png";
import { get_getFa, getAvatarList, putUpdateProfile } from "@/api/uaapi/user";
import { IFaDataItem } from "@/store/user";
const { Big, moment, getUrl } = Util;
const { useTranslation } = Hooks;
const { asyncAwait } = axios;
const { XtAxios } = Request;
const REFERRAL_API_URL = `${Util.getOrigin("www")}/rfapi`;
const REFERRAL_LEVEL_BADGES = [Lv1Img, Lv2Img, Lv3Img, Lv4Img, Lv5Img, Lv6Img, Lv7Img];
const DEFAULT_AVATAR_OPTIONS = Array.from({ length: 6 }, (_, idx) => ({
  id: idx + 1,
  url: "",
  label: `${idx + 1}`.padStart(2, "0"),
}));

/** 无用户头像时优先用 avatarList 中该 id 的 url（与接口约定首条为 id=1）；列表未返回 url 前用本地图兜底 */
const DEFAULT_OVERVIEW_PRESET_AVATAR_ID = 1;
/** 与 avatarList 首条 `url` 一致时可填写，在列表拉取失败时仍展示该预设图（可不填） */
const DEFAULT_OVERVIEW_PRESET_AVATAR_URL = "";

interface PageInfoProps extends ObjAny {
  status?: number;
}

interface withdrawLimitsProps {
  createdTime: string;
  grade: number;
  id: number;
  limitAmount: number;
  tenantId: number;
  updatedTime: string;
}

interface InviteVipProgress {
  currentVipLevel?: number;
  nextVipLevel?: number;
  isMaxLevel?: boolean;
  nextLevelRequiredDirectValidUserCount?: number;
  nextLevelRequiredOpenPositionMargin?: number;
  currentDirectValidUserCount?: number;
  currentOpenPositionMargin?: number;
}
interface AvatarOption {
  id: number;
  url: string;
  label?: string;
}

function clampReferralLevel(n?: number | null) {
  const x = Number(n);
  if (!Number.isFinite(x) || x < 1) return 1;
  return Math.min(7, Math.floor(x));
}

function computeInviteVipTotalProgressPercent(vip?: InviteVipProgress | null) {
  if (!vip || typeof vip !== "object") return 0;
  if (vip.isMaxLevel === true) return 100;

  const reqU = Number(vip.nextLevelRequiredDirectValidUserCount);
  const reqM = Number(vip.nextLevelRequiredOpenPositionMargin);
  const curU = Number(vip.currentDirectValidUserCount);
  const curM = Number(vip.currentOpenPositionMargin);

  const r1 = reqU > 0 ? Math.min(1, Math.max(0, curU / reqU)) : 1;
  const r2 = reqM > 0 ? Math.min(1, Math.max(0, curM / reqM)) : 1;

  return Math.min(100, 50 * r1 + 50 * r2);
}

/** 顶部昵称邮箱脱敏：本地段、域名主机段超过 EMAIL_HEADER_STAR_IF_LONGER_THAN 才插入「***」；域名末尾后缀完整保留 */
const EMAIL_HEADER_MASK_STARS = "***";
const EMAIL_HEADER_MAX_DISPLAY_LEN = 32;
/** @ 前本地段、@ 后主机段字符数 ≤ 该值时不加星号；超过则「前2+***+后2」或「前2+***+后缀」 */
const EMAIL_HEADER_STAR_IF_LONGER_THAN = 6;

function maskEmailSegmentMiddle(seg: string, headKeep: number, tailKeep: number): string {
  const s = seg;
  if (s.length <= headKeep + tailKeep) return s;
  return `${s.slice(0, headKeep)}${EMAIL_HEADER_MASK_STARS}${s.slice(-tailKeep)}`;
}

/** 拆出「主机段」与「末尾后缀」（优先匹配多级后缀），用于固定露出 .com 等 */
function splitDomainHostAndSuffix(domain: string): { host: string; suffix: string } {
  const d = domain.trim();
  if (!d) return { host: "", suffix: "" };
  const dl = d.toLowerCase();

  const multiSuffixes = [".co.uk", ".com.au", ".gov.uk", ".com.cn", ".co.jp", ".co.kr", ".com.hk", ".org.uk", ".net.au"];
  for (const suf of multiSuffixes) {
    if (dl.endsWith(suf)) {
      return { host: d.slice(0, d.length - suf.length), suffix: d.slice(d.length - suf.length) };
    }
  }

  if (dl.endsWith(".com")) {
    return { host: d.slice(0, d.length - 4), suffix: d.slice(d.length - 4) };
  }

  const singleSuffixes = [
    ".net",
    ".org",
    ".edu",
    ".gov",
    ".cn",
    ".io",
    ".hk",
    ".jp",
    ".kr",
    ".uk",
    ".tv",
    ".co",
    ".ai",
    ".me",
    ".info",
    ".biz",
    ".us",
    ".de",
    ".fr",
    ".ru",
  ];
  for (const suf of singleSuffixes) {
    if (dl.endsWith(suf)) {
      return { host: d.slice(0, d.length - suf.length), suffix: d.slice(d.length - suf.length) };
    }
  }

  const lastDot = dl.lastIndexOf(".");
  if (lastDot <= 0) return { host: "", suffix: d };
  return { host: d.slice(0, lastDot), suffix: d.slice(lastDot) };
}

/** 域名脱敏：末端后缀完整保留；主机部分超过 EMAIL_HEADER_STAR_IF_LONGER_THAN 则为「前2+***+后缀」 */
function maskDomainKeepSuffix(domain: string): string {
  const { host, suffix } = splitDomainHostAndSuffix(domain);
  if (!suffix) return domain;
  const h = host.replace(/\.$/, "");
  if (!h) return suffix;
  if (h.length <= EMAIL_HEADER_STAR_IF_LONGER_THAN) return `${h}${suffix}`;
  return `${h.slice(0, 2)}${EMAIL_HEADER_MASK_STARS}${suffix}`;
}

function maskEmailForOverviewHeader(email: string): string {
  const trimmed = email.trim();
  const at = trimmed.indexOf("@");
  if (at <= 0 || at >= trimmed.length - 1) return trimmed;

  const local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1);

  let maskedLocal =
    local.length <= EMAIL_HEADER_STAR_IF_LONGER_THAN ? local : maskEmailSegmentMiddle(local, 2, 2);

  let maskedDomain = maskDomainKeepSuffix(domain);

  let result = `${maskedLocal}@${maskedDomain}`;
  if (result.length <= EMAIL_HEADER_MAX_DISPLAY_LEN) return result;

  if (local.length > EMAIL_HEADER_STAR_IF_LONGER_THAN) {
    maskedLocal = maskEmailSegmentMiddle(local, 1, 1);
    result = `${maskedLocal}@${maskedDomain}`;
    if (result.length <= EMAIL_HEADER_MAX_DISPLAY_LEN) return result;
  }

  return result;
}

const UserOverview: React.FC = () => {
  const t = useTranslation();
  const { push, locale } = useRouter();
  // store
  const {
    user: { userInfo, isSubAcc, updateState, token },
  } = store;
  // useState
  const [pageInfo, setPageInfo] = useState<PageInfoProps>({});
  const [limitList, setLimitList] = useState<withdrawLimitsProps[]>([]);
  const [vipProgress, setVipProgress] = useState<InviteVipProgress | null>(null);
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [nickNameValue, setNickNameValue] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [selectedAvatarId, setSelectedAvatarId] = useState<number | null>(DEFAULT_AVATAR_OPTIONS[0].id);
  const [avatarOptions, setAvatarOptions] = useState<AvatarOption[]>(DEFAULT_AVATAR_OPTIONS);

  const [faDatas, setFaDatas] = useState<IFaDataItem[]>([]);
  const [registrationInfoVisible, setRegistrationInfoVisible] = useState(true);
  const isGuest = !userInfo || !Object.keys(userInfo).length;

  // useEffect
  useEffect(() => {
    if (!token) return;
    store.user.getUserInfo();
    getFaDatas();
    // handleEnterpriseApplyReason();
    getKycWithdrawLimit();
    getInviteVipProgress();
    fetchAvatarList();
  }, [token]);

  const fetchAvatarList = async () => {
    const { data, err } = await waitRes(getAvatarList, {});
    if (err) return;
    const rawList = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
    if (!rawList.length) return;
    const list = rawList
      .filter((item) => Number(item?.id) > 0)
      .map((item) => {
        const id = Number(item.id);
        const url = `${item.url || item.avatarUrl || ""}`.trim();
        return {
          id,
          url,
          label: `${id}`.padStart(2, "0"),
        };
      });
    if (list.length) setAvatarOptions(list);
  };

  const getFaDatas = async () => {
    const { data, err } = await waitRes(get_getFa, {});
    if (err) return;
    updateState({ faData: data });
    setFaDatas(data || []);
  };

  const getInviteVipProgress = async () => {
    try {
      const data = await XtAxios.get(`${REFERRAL_API_URL}/profile/invite/v2/vip-progress`, {
        errorPop: false,
      });
      setVipProgress(data ?? null);
    } catch (error) {
      setVipProgress(null);
    }
  };

  const authLen = useMemo(() => {
    return faDatas.filter((item: IFaDataItem) => !!item.enable).length;
  }, [faDatas]);

  // useMemo
  const handleRenderLevel = useMemo(() => {
    if (isSubAcc) {
      if (authLen == 1) {
        return "level3";
      }
      return "level1";
    } else {
      if (authLen == 1) {
        return "level1";
      } else if (authLen == 2) {
        return "level2";
      } else if (authLen == 3) {
        return "level3";
      }
      return "";
    }
  }, [authLen, isSubAcc]);

  const handleGetAuthLevel = useMemo(() => {
    if (!userInfo) return;
    if (userInfo.authLevel == 3) {
      return t("account.enterprise");
    } else if ([1, 2].includes(userInfo.authLevel!)) {
      return t("account.person");
    } else {
      return t("account.person");
    }
  }, [userInfo]);

  // 认证标签（Verified / Enterprise / Unverified）暂不展示，保留逻辑便于恢复
  // const authTag = useMemo(() => {
  //   const authLevel = Number(userInfo?.authLevel || 0);
  //   if (authLevel === 3) {
  //     return {
  //       className: styles["overview-tag-enterprise"],
  //       text: t("user.enterpriseVerified") || "Enterprise Verified",
  //     };
  //   }
  //   if ([1, 2].includes(authLevel)) {
  //     return {
  //       className: styles["overview-tag-verified"],
  //       text: t("user.verified") || "Verified",
  //     };
  //   }
  //   return {
  //     className: styles["overview-tag-unverified"],
  //     text: t("user.unverified") || "Unverified",
  //   };
  // }, [styles, t, userInfo?.authLevel]);

  const userUid = useMemo(() => {
    if (!userInfo) return "";
    return (userInfo.userAccountLevel! > 1 ? userInfo.userId : userInfo.accountId) ?? "";
  }, [userInfo]);

  const referralVipLevel = useMemo(() => {
    return clampReferralLevel(vipProgress?.currentVipLevel);
  }, [vipProgress]);

  const referralVipBadge = useMemo(() => {
    return REFERRAL_LEVEL_BADGES[referralVipLevel - 1] || REFERRAL_LEVEL_BADGES[0];
  }, [referralVipLevel]);

  const referralVipProgressPct = useMemo(() => {
    return computeInviteVipTotalProgressPercent(vipProgress);
  }, [vipProgress]);

  const currentNickName = useMemo(() => {
    return `${userInfo?.nickname || userInfo?.nickName || ""}`.trim();
  }, [userInfo?.nickName, userInfo?.nickname]);

  /** 顶部头像：用户信息 > 预设列表 id=1（写死）的 url > 列表中 id 升序首条有 url 的项 > 本地占位图 */
  const overviewAvatarDisplayUrl = useMemo(() => {
    const fromUser = `${userInfo?.avatarUrl || userInfo?.headImgUrl || ""}`.trim();
    if (fromUser) return fromUser;
    const byDefaultId = avatarOptions.find(
      (a) => a.id === DEFAULT_OVERVIEW_PRESET_AVATAR_ID && `${a.url || ""}`.trim()
    );
    if (byDefaultId?.url) return `${byDefaultId.url}`.trim();
    const sorted = [...avatarOptions].filter((a) => Number(a.id) > 0).sort((a, b) => a.id - b.id);
    const firstWithUrl = sorted.find((a) => `${a.url || ""}`.trim());
    if (firstWithUrl?.url) return `${firstWithUrl.url}`.trim();
    const staticFallback = `${DEFAULT_OVERVIEW_PRESET_AVATAR_URL}`.trim();
    if (staticFallback) return staticFallback;
    return overviewDefaultHeadImg as string;
  }, [userInfo?.avatarUrl, userInfo?.headImgUrl, avatarOptions]);

  const nickNameTrimmed = useMemo(() => nickNameValue.trim(), [nickNameValue]);
  const nicknamePattern = /^[a-zA-Z0-9_.-]{1,20}$/;
  const nicknameInvalid = useMemo(() => !!nickNameTrimmed && !nicknamePattern.test(nickNameTrimmed), [nickNameTrimmed]);
  const saveDisabled = !nickNameTrimmed || profileSaving || !selectedAvatarId || nicknameInvalid;

  const getFeeSecond = useMemo(() => {
    if (!userInfo) return t("account.NoLimit");
    const rest =
      limitList.find((ii) => ii.grade == userInfo.authLevel)?.limitAmount || limitList.find((ii) => ii.grade == userInfo.authLevel)?.limitAmount == 0
        ? limitList.find((ii) => ii.grade == userInfo.authLevel)?.limitAmount + " USD/" + t("account.periodDay")
        : t("account.NoLimit");
    return rest;
  }, [limitList, userInfo]);

  // 企业认证信息
  const handleEnterpriseApplyReason = async () => {
    const { err, data } = await asyncAwait(get_enterpriseApplyReason);
    if (err) return;
    setPageInfo(data);
  };

  const getKycWithdrawLimit = async () => {
    const defaultResWallet = [
      { grade: 0, limitAmount: 0, id: 1 },
      { grade: 1, limitAmount: 0, id: 2 },
      { grade: 2, limitAmount: 0, id: 3 },
      { grade: 3, limitAmount: 0, id: 4 },
    ];
    const { err, data } = await asyncAwait(get_kycWalletLimit);
    if (err) return;
    const list = data && data.length != 0 ? data : defaultResWallet;
    setLimitList(list);
  };

  // const handleGetPriceUnit = (val: number) => {
  //   if (val / 10000 >= 1) {
  //     return Big(val).div(10000) + t("account.priceUnit");
  //   }
  //   return val || {};
  // };

  const routeTo = (url: string) => {
    push(url);
  };

  const routeToRate = () => {
    window.location.href = getUrl("/rate");
  };

  const jump = (url: string) => {
    if (!url) return;
    window.location.href = url;
  };

  const handleCopyOrderNum = async (text: string | number) => {
    try {
      await copy(String(text));
      message.success(t("account.copy"));
    } catch (error) {
      message.error("error");
    }
  };

  // 安全退出登录
  const handleLogout = () => {
    XtAxios.get(getUrl("/uaapi/uaa/authorize/token/revoke"))
      .then(() => {
        Storage.remove("token");
        Cookie.remove("token");
        Storage.remove("bindGoogleTip");
        Storage.remove("otcFiat");
        location.reload();
      })
      .catch(() => undefined)
      .finally(() => undefined);
  };

  const routeToSpikexCard = () => {
    const lang = !locale || locale === "default" ? "en" : locale;
    jump(getUrl(`/${lang}/spikex-card`));
  };

  // 设计稿 app 版本：menuItems 必须在 early return 之前调用（hooks 规则）
  const menuItems = useMemo(() => {
    const items: Array<{ key: string; label: string; icon: string; onClick: () => void; right?: string }> = [
      { key: "invite", label: t("header.superPartner") || t("account.ReferFriends"), icon: InviteImg, onClick: () => jump(getUrl("/referral")) },
      { key: "fee", label: t("account.feeTit") || "Fee discount", icon: FeeImg, onClick: routeToRate },
      { key: "payment", label: t("account.paymentMethod"), icon: PaymentMethodImg, onClick: () => routeTo("/accounts/user/payment-method") },
      { key: "security", label: t("account.safeCenter"), icon: SafetyImg, onClick: () => routeTo("/accounts/user/security-center") },
      { key: "setting", label: t("account.Set") || "Settings", icon: Geren, onClick: () => routeTo("/accounts/user/setting") },
      // { key: "api", label: t("account.apiManagement"), onClick: () => routeTo("/accounts/user/api") },
      { key: "about", label: t("account.about"), icon: ApiImg, onClick: () => routeTo("/accounts/user/about") },
    ];
    if (isGuest || !(userInfo.userAccountLevel! > 1)) {
      items.splice(4, 0, { key: "cert", label: t("account.Authentication"), icon: CertificationImg, onClick: () => routeTo("/accounts/user/verification") });
    }
    return items;
  }, [isGuest, userInfo, t]);

  // 设计稿 app 版本：头像+邮箱+UID、双按钮、列表行（图标+文案+右箭头）
  const CHEVRON_RIGHT = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const maskedMobile =
    userInfo?.mobile &&
    `+${+userInfo.mobileCountryCode! || +userInfo.countryCode!} ${`${userInfo.mobile}`.length > 7 ? userInfo.mobile!.replace(/(\d{3})\d*(\d{4})/, "$1***$2") : userInfo.mobile!.replace(/(\d{2})\d*(\d{2})/, "$1***$2")}`;
  const maskedEmail = userInfo?.email && userInfo.email.replace(/(\w{2}).*(\w{1})@(.*)/, "$1***@$3");
  /** 顶部昵称行：邮箱类走 maskEmailForOverviewHeader；手机仍用原 maskedMobile */
  const headerSourceRaw =
    currentNickName ||
    (userInfo?.userAccountLevel === 2
      ? `${userInfo?.userName ?? ""}`
      : userInfo?.mobile
        ? `${maskedMobile ?? ""}`
        : userInfo?.email
          ? `${userInfo.email}`
          : `${t("account.unverified")}`);

  const overviewHeaderMasked =
    typeof headerSourceRaw === "string" && headerSourceRaw.includes("@")
      ? maskEmailForOverviewHeader(headerSourceRaw)
      : headerSourceRaw;

  const registrationInfo = userInfo?.email
    ? registrationInfoVisible
      ? userInfo.email
      : maskedEmail
    : userInfo?.mobile
      ? registrationInfoVisible
        ? userInfo.mobile
        : maskedMobile
      : "-";

  const handleGoLogin = () => {
    window.location.href = getUrl("/accounts/login?backUrl=" + encodeURIComponent(location.href));
  };

  const handleGoRegister = () => {
    window.location.href = getUrl("/accounts/register");
  };

  const openProfileDrawer = () => {
    if (isGuest) return;
    const currentAvatarId = Number(userInfo?.avatarId || 0);
    const currentAvatarUrl = `${userInfo?.avatarUrl || userInfo?.headImgUrl || ""}`.trim();
    const currentAvatar = avatarOptions.find((item) => item.id === currentAvatarId || (!!item.url && item.url === currentAvatarUrl));
    setSelectedAvatarId(currentAvatar?.id || avatarOptions[0]?.id || DEFAULT_AVATAR_OPTIONS[0].id);
    setNickNameValue(currentNickName);
    setProfileDrawerOpen(true);
  };

  const closeProfileDrawer = () => {
    if (profileSaving) return;
    setProfileDrawerOpen(false);
  };

  const saveProfile = async () => {
    const value = nickNameValue.trim();
    if (!value || profileSaving || !selectedAvatarId) return;
    if (!nicknamePattern.test(value)) {
      return;
    }
    const currentAvatarId = Number(userInfo?.avatarId || 0);
    const nextPayload: ObjAny = {};
    if (value !== currentNickName) nextPayload.nickname = value;
    if (selectedAvatarId !== currentAvatarId) nextPayload.avatarId = selectedAvatarId;
    if (!Object.keys(nextPayload).length) {
      setProfileDrawerOpen(false);
      return;
    }
    setProfileSaving(true);
    try {
      await putUpdateProfile({
        data: nextPayload,
        successPop: true,
        errorPop: true,
      });
      await store.user.getUserInfo();
      setProfileDrawerOpen(false);
    } finally {
      setProfileSaving(false);
    }
  };

  return (
    <div className={styles["overview-page"]}>
      <div className={isGuest ? styles["overview-guest-wrapper"] : styles["overview-card"]}>
        {!isGuest && <img className={styles["overview-level-badge"]} src={referralVipBadge} alt={`VIP ${referralVipLevel}`} />}
        {!isGuest ? (
          <>
            <div className={styles["overview-user-block"]}>
              <button type="button" className={styles["overview-avatar-button"]} onClick={openProfileDrawer} aria-label={t("account.headSculpture")}>
                <div className={styles["overview-avatar"]} aria-hidden="true">
                  <img className={styles["overview-avatar-image"]} src={overviewAvatarDisplayUrl} alt="" />
                </div>
              </button>
              <div className={styles["overview-user-main"]}>
                <div className={styles["overview-account-row"]}>
                  <span
                    className={styles["overview-account-text"]}
                    title={headerSourceRaw.includes("@") ? headerSourceRaw : undefined}
                  >
                    <span
                      className={cx(
                        styles["overview-account-textInner"],
                        headerSourceRaw.includes("@") && styles["overview-account-textInnerForcedLtr"]
                      )}
                    >
                      {overviewHeaderMasked}
                    </span>
                  </span>
                  <button type="button" className={styles["overview-edit-button"]} onClick={openProfileDrawer} aria-label={t("account.myProfileModalTitle")}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M8.11728 2.48855L9.49593 3.8672L9.69204 3.67109C9.90673 3.45629 9.9068 3.1083 9.69204 2.89355L9.09093 2.29244C8.87617 2.07775 8.52816 2.07777 8.31339 2.29244L8.11728 2.48855ZM7.67706 1.65611C8.24331 1.08997 9.16103 1.08995 9.72726 1.65611L10.3284 2.25722C10.8946 2.82345 10.8949 3.74149 10.3287 4.30776L5.81815 8.81833C5.64241 8.994 5.35753 8.9937 5.18182 8.81798C5.00611 8.64227 5.00581 8.3574 5.18147 8.18166L8.8596 4.50353L7.48095 3.12488L3.78763 6.8182C3.6119 6.99393 3.3267 6.99393 3.15096 6.8182C2.97529 6.64245 2.97559 6.35758 3.1513 6.18187L7.67706 1.65611Z" fill="#9C9C9C"/>
                      <path d="M3.35851 9.35821C2.94784 9.37893 2.60549 9.03657 2.6262 8.6259L2.63621 8.53717L2.98597 6.55706C3.08443 5.99944 3.7698 5.78317 4.17023 6.18348L5.80093 7.81418C6.20123 8.21461 5.98497 8.89998 5.42735 8.99844L3.44724 9.3482L3.35851 9.35821ZM4.89253 8.17913L3.80529 7.09188L3.57223 8.41253L4.89253 8.17913Z" fill="#9C9C9C"/>
                      <path d="M10.5 9.90918C10.7485 9.90918 10.9502 10.1108 10.9502 10.3594C10.9502 10.6079 10.7485 10.8096 10.5 10.8096H1.5C1.25147 10.8096 1.0498 10.6079 1.0498 10.3594C1.0498 10.1108 1.25147 9.90918 1.5 9.90918H10.5Z" fill="#9C9C9C"/>
                    </svg>
                  </button>
                </div>
                {/* {authTag ? <span className={authTag.className}>{authTag.text}</span> : null} */}
              </div>
            </div>

            <div className={styles["overview-meta"]}>
              <div className={styles["overview-kv"]}>
                <div className={styles["overview-kv-row"]}>
                  <span className={styles["overview-kv-key"]}>UID</span>
                  <span className={styles["overview-kv-val"]}>
                    <span className={styles["overview-kv-val-text"]}>{userUid || "--"}</span>
                    <span
                      className={styles["copy-box"]}
                      role="button"
                      tabIndex={0}
                      onClick={() => userUid && handleCopyOrderNum(userUid)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          if (userUid) handleCopyOrderNum(userUid);
                        }
                      }}
                      aria-label={t("account.copy")}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                        <path d="M10.3749 1C10.7201 1 11 1.27982 11 1.625L11.0006 8.125C11.0007 8.47018 10.7209 8.75 10.3757 8.75H9.62569C9.41858 8.75 9.25067 8.58211 9.25065 8.375C9.25063 8.16789 9.41851 8 9.62562 8H10.2506L10.25 1.75H3.99997L3.99997 2.37504C3.99997 2.58214 3.83208 2.75002 3.62497 2.75C3.41787 2.74998 3.24997 2.58207 3.24997 2.37496L3.24997 1.62494C3.24997 1.27978 3.52976 1 3.87491 1H10.3749Z" fill="#757575" />
                        <path fillRule="evenodd" clipRule="evenodd" d="M8.12494 3.25C8.47012 3.25 8.74997 3.52982 8.75 3.875L8.75063 10.375C8.75066 10.7202 8.47087 11 8.12569 11H1.62569C1.28051 11 1.00066 10.7202 1.00063 10.375L1 3.875C0.999967 3.52982 1.27976 3.25 1.62494 3.25H8.12494ZM8.00062 10.25L8.00001 4H1.75001L1.75062 10.25H8.00062Z" fill="#757575" />
                      </svg>
                    </span>
                  </span>
                </div>
                <div className={styles["overview-kv-row"]}>
                  <span className={styles["overview-kv-key"]}>{t("account.registrationInfo")}</span>
                  <span className={styles["overview-kv-val"]}>
                    <span className={styles["overview-kv-val-text"]}>{registrationInfo}</span>
                    {(userInfo.email || userInfo.mobile) ? (
                      <button type="button" className={styles["overview-eye"]} onClick={() => setRegistrationInfoVisible(!registrationInfoVisible)} aria-label="toggle visibility">
                        {registrationInfoVisible ? (
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                            <path d="M8 10.5C9.10457 10.5 10 9.60457 10 8.5C10 7.39543 9.10457 6.5 8 6.5C6.89543 6.5 6 7.39543 6 8.5C6 9.60457 6.89543 10.5 8 10.5Z" stroke="currentColor" strokeWidth="1.2" />
                            <path d="M1 8C3 4 5 2 8 2C11 2 13 4 15 8C13 12 11 14 8 14C5 14 3 12 1 8Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                            <path d="M11.5 11.5C10 13 6.5 14 4 14C2 14 1 13 1 13M5 6C5 7.5 6.5 9 8 9M10 3C12 4 14 6.5 14 8C14 8.5 13.5 9.5 12.5 10.5M2 2L14 14" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </button>
                    ) : null}
                  </span>
                </div>
              </div>

              <div className={styles["overview-divider"]} />

              <div className={styles["overview-kv"]}>
                <div className={styles["overview-vip-row"]}>
                  <div className={styles["overview-vip-left"]}>
                    <span className={styles["overview-vip-label"]}>{`VIP${referralVipLevel}`}</span>
                    <span className={styles["overview-vip-bar"]} aria-hidden="true">
                      <span className={styles["overview-vip-bar-fill"]} style={{ width: `${referralVipProgressPct}%` }} />
                    </span>
                  </div>
                  <button
                    type="button"
                    className={styles["overview-vip-link"]}
                    onClick={() => jump(getUrl("/affiliate"))}
                  >
                    {(t("user.benefits") || "Benefits")} {CHEVRON_RIGHT}
                  </button>
                </div>
                <div className={styles["overview-vip-hint"]}>{t("user.vipHint") || "Trade more to reach the next level"}</div>
              </div>
            </div>

            <div
              role="button"
              tabIndex={0}
              className={styles["overview-spikex-card-entry"]}
              onClick={routeToSpikexCard}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  routeToSpikexCard();
                }
              }}
            >
              <span className={styles["overview-spikex-card-icon"]} aria-hidden="true">
                <img src={SpikexCardImg} alt="" />
              </span>
              <span className={styles["overview-spikex-card-label"]}>{t("user.spikexCard") || "Spikex Card"}</span>
              <span className={styles["overview-spikex-card-action"]}>{t("user.spikexCardApply") || "Apply Now"}</span>
            </div>
          </>
        ) : (
          <div className={styles["overview-guest-card"]}>
            <div className={styles["overview-user-block"]}>
              <div className={cx(styles["overview-avatar"], styles["overview-avatar-guest"])} aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" fill="none">
                  <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="#7D838C"/>
                  <path d="M4 20C4 16.6863 7.58172 14 12 14C16.4183 14 20 16.6863 20 20" stroke="#7D838C" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className={styles["overview-user-main"]}>
                <div className={styles["overview-account-row"]}>
                  <button type="button" className={styles["overview-auth-link"]} onClick={handleGoLogin}>
                    {t("header.login") || t("app.login") || "Log in"}
                  </button>
                  <span className={styles["overview-auth-separator"]}>/</span>
                  <button type="button" className={styles["overview-auth-link"]} onClick={handleGoRegister}>
                    {t("app.register") || "Register"}
                  </button>
                </div>
                <div className={styles["overview-guest-tip"]}>{t("user.guestWelcome") || "Hi Welcome to Spikex"}</div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className={styles["overview-list"]}>
        {menuItems.map((item) => (
          <div key={item.key} role="button" tabIndex={0} className={styles["overview-list-item"]} onClick={item.onClick} onKeyDown={(e) => e.key === "Enter" && item.onClick()}>
            <span className={styles["overview-list-icon"]} aria-hidden="true">
              <img src={item.icon} alt="" />
            </span>
            <span className={styles["overview-list-label"]}>{item.label}</span>
            {item.right && <span className={styles["overview-list-right"]}>{item.right}</span>}
            <span className={styles["overview-list-chevron"]}>{CHEVRON_RIGHT}</span>
          </div>
        ))}
      </div>
      {!isGuest && (
        <div className={styles.logoutSection}>
          <button className={styles.logoutButton} type="button" onClick={handleLogout}>
            {t("header.safelyLogout") || "Safely Logout"}
          </button>
        </div>
      )}
      <Drawer
        open={profileDrawerOpen}
        placement="bottom"
        height="auto"
        closable={false}
        className={styles["overview-nickname-drawer"]}
        onClose={closeProfileDrawer}
        destroyOnClose
      >
        <div className={styles["overview-nickname-sheet"]}>
          <div className={styles["overview-sheet-handle-wrap"]}>
            <div className={styles["overview-sheet-handle"]} />
          </div>
          <div className={styles["overview-sheet-header"]}>
            <span className={styles["overview-sheet-title"]}>{t("account.overviewProfileDrawerTitle")}</span>
          </div>
          <div className={styles["overview-avatar-selector"]}>
            <div className={styles["overview-sheet-label"]}>{t("account.chooseAvatar")}</div>
            <div className={styles["overview-avatar-scroll"]}>
              {avatarOptions.map((avatar) => {
                const checked = selectedAvatarId === avatar.id;
                return (
                  <button
                    key={avatar.id}
                    type="button"
                    className={cx(styles["overview-avatar-option"], checked && styles["overview-avatar-option-active"])}
                    onClick={() => setSelectedAvatarId(avatar.id)}
                    aria-label={`${t("account.headSculpture")} ${avatar.label}`}
                  >
                    <span className={styles["overview-avatar-option-inner"]}>
                      {avatar.url ? <img className={styles["overview-avatar-option-image"]} src={avatar.url} alt="" /> : avatar.label || avatar.id}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className={styles["overview-sheet-label"]}>{t("account.myProfileTitle")}</div>
          <Input
            className={styles["overview-sheet-input"]}
            maxLength={20}
            value={nickNameValue}
            placeholder={t("account.nicknameLengthPlaceholder")}
            onChange={(e) => setNickNameValue(e.target.value)}
          />
          {nicknameInvalid ? <p className={styles["overview-sheet-error"]}>{t("account.nicknameFormatInvalid")}</p> : null}
          <ul className={styles["overview-sheet-tip-list"]}>
            <li>{t("account.nicknameModifyTip")}</li>
            <li>{t("account.nicknameCharsTip")}</li>
          </ul>
          <div className={styles["overview-sheet-actions"]}>
            <button
              type="button"
              className={styles["overview-sheet-confirm"]}
              onClick={saveProfile}
              disabled={saveDisabled}
            >
              {t("userCenter.save") || "Save"}
            </button>
            <button type="button" className={styles["overview-sheet-cancel"]} onClick={closeProfileDrawer} disabled={profileSaving}>
              {t("account.cancel")}
            </button>
          </div>
        </div>
      </Drawer>
    </div>
  );
};
export default observer(UserOverview);
